import { MetadataOptionWithCommentsAnswer, WebFormsState } from '../types';

/**
 * Remaps saved keys of a `select_option_metadata_comments` answer to the
 * current metadata item names (matching by trim) and drops keys that no
 * longer match any item. Keeps edits working after the metadata list is
 * renamed and prevents orphan keys from polluting sync.
 *
 * Returns `null` for no-op, `undefined` when every saved key was orphaned
 * (so required validation marks the answer unanswered), or the remapped
 * answer object.
 */
export function normalizeMetadataOptionWithCommentsValue(
  value: unknown,
  question: { type?: string; metadata_code?: string } | undefined,
  metadata: WebFormsState['metadata'],
): MetadataOptionWithCommentsAnswer | undefined | null {
  if (question?.type !== 'select_option_metadata_comments') return null;
  const metadataItems = question.metadata_code ? metadata[question.metadata_code] : undefined;
  // Without metadata loaded we can't tell valid keys from orphans -- bail.
  if (!metadataItems?.length) return null;
  if (!value || typeof value !== 'object') return null;
  const typedValue = value as MetadataOptionWithCommentsAnswer;

  // Build two lookups against the current metadata:
  //   exactNames: names that match a saved key character-for-character.
  //   trimToName: `trimmed name -> current name`, or `null` when two items
  //               collapse to the same trim (e.g. "FOO" and "FOO ").
  const exactNames = new Set<string>();
  const trimToName = new Map<string, string | null>();
  metadataItems.forEach((item) => {
    if (typeof item.name !== 'string') return;
    exactNames.add(item.name);
    const trimmed = item.name.trim();
    trimToName.set(trimmed, trimToName.has(trimmed) ? null : item.name);
  });

  let changed = false;
  const normalizedValue: MetadataOptionWithCommentsAnswer = {};
  Object.keys(typedValue).forEach((key) => {
    // Exact match wins so trim-equivalent metadata items stay disambiguated.
    if (exactNames.has(key)) {
      normalizedValue[key] = typedValue[key];
      return;
    }
    const currentName = trimToName.get(key.trim());
    // Drop when the key is orphaned (`undefined`) or trim-ambiguous (`null`).
    if (!currentName) {
      changed = true;
      return;
    }
    if (currentName !== key) changed = true;
    normalizedValue[currentName] = typedValue[key];
  });

  if (!changed) return null;
  // Empty result -> undefined so required validation flags it as unanswered.
  return Object.keys(normalizedValue).length === 0 ? undefined : normalizedValue;
}

export function isEmptyAnswerValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;

  // Primitive types
  if (typeof value === 'string') return value.trim().length === 0;
  if (typeof value === 'number') return false;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'bigint' || typeof value === 'symbol') return false;
  if (typeof value === 'function') return false;

  // Dates
  if (value instanceof Date) return isNaN(value.getTime());

  // Files/Blobs - consider any present file/blob as non-empty
  if (typeof File !== 'undefined' && value instanceof File) return value.size === 0;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size === 0;

  // Arrays
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    return value.every((item) => isEmptyAnswerValue(item));
  }

  // Maps/Sets
  if (value instanceof Map) {
    if (value.size === 0) return true;
    for (const [, v] of value) {
      if (!isEmptyAnswerValue(v)) return false;
    }
    return true;
  }
  if (value instanceof Set) {
    if (value.size === 0) return true;
    for (const v of value) {
      if (!isEmptyAnswerValue(v)) return false;
    }
    return true;
  }

  // Plain objects
  if (isPlainObject(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return true;
    // Typical repeatable structure empty shape: { 0: {} }
    // General rule: object is empty when all values are empty
    return entries.every(([, v]) => isEmptyAnswerValue(v));
  }

  // Fallback: treat unknown structured data as non-empty
  return false;
}

function isPlainObject(input: unknown): input is Record<string, unknown> {
  if (Object.prototype.toString.call(input) !== '[object Object]') return false;
  const prototype = Object.getPrototypeOf(input);
  return prototype === null || prototype === Object.prototype;
}

/**
 * Checks whether any file uploads are still in progress.
 * Works with both per-answer pending counts (for standalone photo/file questions)
 * and the global pendingUploadsMap (for repeatable sub-questions and checklist metadata).
 */
export function hasPendingFileUploads(
  answers: Record<string, { pendingUploads?: number }>,
  pendingUploadsMap?: Record<string, number>,
): boolean {
  const hasAnswerPending = Object.values(answers).some(
    (answer) => (answer.pendingUploads ?? 0) > 0,
  );
  const hasMapPending = pendingUploadsMap
    ? Object.values(pendingUploadsMap).some((count) => count > 0)
    : false;
  return hasAnswerPending || hasMapPending;
}
